import * as sdk from './client/sdk.gen';
import { createClient } from '@hey-api/client-fetch';
import { EnhancedUser } from './EnhancedUser';
import { OtpChecker } from './otp-checker';
import { TokenManager } from './token-manager';
import { TypedEventEmitter } from './TypedEventEmitter';
export var AuthAction;
(function (AuthAction) {
    AuthAction["INITIALIZED"] = "initialized";
    AuthAction["SIGNUP"] = "signup";
    AuthAction["SIGNIN"] = "signin";
    AuthAction["LOGIN"] = "login";
    AuthAction["LOGOUT"] = "logout";
    AuthAction["PASSWORD_RESET"] = "password_reset";
    AuthAction["SESSION_RESTORED"] = "session_restored";
    AuthAction["SESSION_EXPIRED"] = "session_expired";
    AuthAction["IMPERSONATE_STARTED"] = "impersonate_started";
    AuthAction["IMPERSONATE_STOPPED"] = "impersonate_stopped";
})(AuthAction || (AuthAction = {}));
let globalAuthService = null;
export class AuthService {
    constructor(options) {
        this.options = options;
        this.eventEmitter = new TypedEventEmitter();
        this.options = options;
        this.isInitialized = false;
        this.on = this.eventEmitter.on.bind(this.eventEmitter);
        this.emit = this.eventEmitter.emit.bind(this.eventEmitter);
        this.off = this.eventEmitter.off.bind(this.eventEmitter);
        this.once = this.eventEmitter.once.bind(this.eventEmitter);
        this.client = createClient({
            baseUrl: this.options.baseUrl,
            credentials: 'include',
            headers: {
                'X-App-Id': this.options.appId,
            },
            ...this.options.clientConfig,
        });
        this.tokenManager = new TokenManager(this, this.options.tokenManagerOptions);
        this.tokenManager.onTokenRefreshed(({ token }) => {
            this.emit('tokenChanged', { token });
        });
        this.tokenManager.onTokenError(({ error }) => {
            console.error('Token refresh failed:', error);
            this.emit('tokenError', { error });
            this.logout().catch(console.error);
        });
        this.on('authStateChanged', this.handleAuthStateChanged.bind(this));
        sdk
            .postSession({ client: this.client })
            .then(({ data, error }) => {
            if (data) {
                const { accessToken, payload } = data;
                this.emit('authStateChanged', { action: AuthAction.SESSION_RESTORED, user: new EnhancedUser(this, payload.user) });
                if (accessToken) {
                    this.emit('tokenChanged', { token: accessToken });
                }
                return data;
            }
            if (error && error.type === 'session_expired') {
                this.emit('authStateChanged', { action: AuthAction.SESSION_EXPIRED, user: undefined, previousUser: this._currentUser });
            }
        })
            .finally(() => {
            this.isInitialized = true;
            this.emit('authStateChanged', { action: AuthAction.INITIALIZED, user: this._currentUser });
        });
    }
    async waitForInitialization() {
        if (!this.isInitialized) {
            await new Promise(resolve => {
                const checkInterval = setInterval(() => {
                    if (this.isInitialized) {
                        clearInterval(checkInterval);
                        resolve();
                    }
                }, 50);
            });
        }
    }
    get currentUser() {
        return this._currentUser;
    }
    set currentUser(user) {
        if (user) {
            this._currentUser = user;
        }
        else {
            this._currentUser = undefined;
        }
    }
    static initialize(options) {
        globalAuthService = new AuthService(options);
        return globalAuthService;
    }
    static default() {
        if (!globalAuthService) {
            throw new Error('AuthService not initialized');
        }
        return globalAuthService;
    }
    handleAuthStateChanged(event) {
        if (event.action === AuthAction.SESSION_EXPIRED) {
            this.logout().catch(console.error);
        }
        if ([AuthAction.SESSION_RESTORED, AuthAction.LOGIN].includes(event.action) && event.user) {
            this.currentUser = event.user;
        }
        if (event.action === AuthAction.LOGOUT) {
            this.currentUser = undefined;
        }
    }
    currentToken() {
        return this.tokenManager.currentToken();
    }
    async me() {
        const { data, error } = await sdk.getMe({ client: this.client });
        if (error) {
            if (error.type === 'token_expired') {
                this.refreshToken()
                    .then(({ accessToken }) => {
                    this.emit('tokenChanged', { token: accessToken });
                    return this.me();
                })
                    .catch(console.error);
            }
            throw error;
        }
        return data;
    }
    async signUpWithUsernameAndPassword(body) {
        const { data, error } = await sdk.postSignUpWithUsernamePassword({
            client: this.client,
            body,
        });
        if (!data) {
            throw error || new Error('Sign up failed');
        }
        const { accessToken, user } = data;
        const enhancedUser = new EnhancedUser(this, user);
        this.emit('authStateChanged', { action: AuthAction.SIGNUP, user: enhancedUser });
        if (accessToken) {
            this.emit('authStateChanged', { action: AuthAction.LOGIN, user: enhancedUser });
            this.emit('tokenChanged', { token: accessToken });
        }
        return data;
    }
    async loginWithUsernameAndPassword(body) {
        const { data, error } = await sdk.postSignInUsernamePassword({
            client: this.client,
            body,
        });
        if (!data) {
            throw error || new Error('Login failed - missing data');
        }
        const { accessToken, user } = data;
        this.emit('authStateChanged', { action: AuthAction.LOGIN, user: new EnhancedUser(this, user) });
        if (accessToken) {
            this.emit('tokenChanged', { token: accessToken });
        }
        return data;
    }
    async loginWithActionCode(body) {
        const { data, error } = await sdk.postSignInActionCode({
            client: this.client,
            body,
        });
        if (!data) {
            throw error || new Error('Login failed - missing data');
        }
        const { accessToken, user } = data;
        this.emit('authStateChanged', { action: AuthAction.LOGIN, user: new EnhancedUser(this, user) });
        if (accessToken) {
            this.emit('tokenChanged', { token: accessToken });
        }
        return data;
    }
    async refreshToken() {
        const { data, error } = await sdk.postRefreshToken({ client: this.client });
        if (error) {
            if (error.type === 'token_expired') {
                this.emit('tokenError', { error: new Error('Refresh Token expired') });
                this.logout().catch(console.error);
            }
            throw error;
        }
        this.emit('tokenChanged', { token: data.accessToken });
        return data;
    }
    async logout() {
        const { data, error } = await sdk.postSignOut({ client: this.client });
        if (!data) {
            throw error || new Error('Logout failed');
        }
        this.emit('authStateChanged', { action: AuthAction.LOGOUT, user: undefined });
        return data;
    }
    async backendIsAlive() {
        const { data, error } = await sdk.getHealthz({ client: this.client });
        if (!data) {
            throw error || new Error('Backend is not alive');
        }
        return data;
    }
    async otpSend(body) {
        const { data, error } = await sdk.postOtpSend({ client: this.client, body });
        if (!data) {
            throw error || new Error('OTP send failed - missing data');
        }
        return new OtpChecker(this, {
            otpRequest: body.otp,
            otpResponse: data,
        });
    }
    async otpVerify(body) {
        const { data, error } = await sdk.postOtpVerify({ client: this.client, body });
        if (!data) {
            throw error || new Error('OTP verify failed - missing data');
        }
        return data;
    }
    async changePassword(body) {
        const { data, error } = await sdk.postChangePassword({ client: this.client, body });
        if (!data) {
            throw error || new Error('Change password failed - missing data');
        }
        return data;
    }
    async resetPassword(body) {
        const { data, error } = await sdk.postResetPassword({ client: this.client, body });
        if (!data) {
            throw error || new Error('Reset password failed - missing data');
        }
        return data;
    }
    async startImpersonate(body) {
        const { data, error } = await sdk.postStartImpersonate({ client: this.client, body });
        if (!data) {
            throw error || new Error('Start impersonate failed - missing data');
        }
        this.emit('authStateChanged', {
            action: AuthAction.IMPERSONATE_STARTED,
            user: new EnhancedUser(this, data.impersonatedUser),
            previousUser: this._currentUser,
        });
        this.emit('tokenChanged', { token: data.accessToken });
        return data;
    }
    async stopImpersonate(body) {
        const { data, error } = await sdk.postStopImpersonate({ client: this.client, body });
        if (!data) {
            throw error || new Error('Stop impersonate failed - missing data');
        }
        this.emit('authStateChanged', {
            action: AuthAction.IMPERSONATE_STOPPED,
            user: new EnhancedUser(this, data.originalUser),
            previousUser: this._currentUser,
        });
        this.emit('tokenChanged', { token: data.accessToken });
        return data;
    }
}
export default AuthService;
//# sourceMappingURL=auth.service.js.map